import { useState, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { canAccessFeature, checkFeatureLimit, FEATURES, FeatureType } from '../config/features';

interface UsePremiumFeatureResult {
  // Доступна ли функция
  hasAccess: boolean;
  // Премиум ли пользователь
  isPremium: boolean;
  // Информация о лимитах (для бесплатных)
  limit?: number;
  remaining?: number;
  // Функция для показа модалки апгрейда
  requestUpgrade: () => void;
  // Проверить лимит использования
  checkLimit: (currentUsage: number) => boolean;
}

/**
 * Хук для проверки доступа к премиум функциям
 * 
 * @example
 * ```tsx
 * const { hasAccess, requestUpgrade } = usePremiumFeature('ai_chat');
 * 
 * if (!hasAccess) {
 *   return <Button onClick={requestUpgrade}>Апгрейд до премиум</Button>;
 * }
 * ```
 */
export const usePremiumFeature = (featureId: FeatureType): UsePremiumFeatureResult => {
  const { settings } = useAppStore();
  const isPremium = settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium';
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const hasAccess = canAccessFeature(featureId, isPremium);
  
  const requestUpgrade = useCallback(() => {
    setShowUpgradeModal(true);
    // Открываем страницу подписки через стор
    const { setCurrentPage } = useAppStore.getState();
    setCurrentPage('subscription');
  }, []);
  
  const checkLimit = useCallback((currentUsage: number): boolean => {
    const result = checkFeatureLimit(featureId, currentUsage, isPremium);
    
    if (!result.allowed) {
      requestUpgrade();
      return false;
    }
    
    return true;
  }, [featureId, isPremium, requestUpgrade]);
  
  const feature = FEATURES[featureId];
  const limitInfo = feature.freeLimit && !isPremium
    ? checkFeatureLimit(featureId, 0, false)
    : undefined;
  
  return {
    hasAccess,
    isPremium,
    limit: limitInfo?.limit,
    remaining: limitInfo?.remaining,
    requestUpgrade,
    checkLimit,
  };
};

/**
 * Хук для получения информации о подписке
 */
export const useSubscription = () => {
  const { settings } = useAppStore();
  
  return {
    isPremium: settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium',
    subscriptionType: settings?.subscription_status || 'free',
    expiresAt: settings?.subscription_expires_at,
  };
};

